import { ChuteScheduleRow } from '@/rtk-query.gql';

export const MINUTES_IN_DAY = 24 * 60;

const toMinutes = (hhmm: number) => Math.floor(hhmm / 100) * 60 + (hhmm % 100);

export const addInterval = (start: number, minutes: number): string => {
  const total = toMinutes(start) + minutes;
  const hh = Math.floor(total / 60) % 24;
  const mm = total % 60;
  return `${hh}${mm.toString().padStart(2, '0')}`.padStart(4, '0');
};

export const overflowWithSequence = (
  rows: number,
  start: number,
  interval: number,
): boolean => toMinutes(start) + interval * (rows - 1) >= MINUTES_IN_DAY;

export const overflowWithChutes = (
  rows: number,
  chuteSize: number,
  start: number,
  interval: number,
): boolean => {
  const chutes = Math.ceil(rows / chuteSize);
  return toMinutes(start) + interval * (chutes - 1) >= MINUTES_IN_DAY;
};

export const buildTimeMap = (
  ids: string[],
  start: number,
  interval: number,
  chuteSize?: number,
): Map<string, string> => {
  const m = new Map<string, string>();
  if (!chuteSize || chuteSize <= 1) {
    ids.forEach((id, idx) => m.set(id, addInterval(start, interval * idx)));
  } else {
    ids.forEach((id, idx) => {
      const chuteIdx = Math.floor(idx / chuteSize);
      m.set(id, addInterval(start, interval * chuteIdx));
    });
  }
  return m;
};

export const sanitizeRows = (
  rows: readonly (ChuteScheduleRow | null | undefined)[],
): ChuteScheduleRow[] =>
  rows.filter((r): r is ChuteScheduleRow => !!r && !!r.aspirationId);

type SiteRow = { aspirationId?: unknown; opuSiteId?: string | null };

const siteKey = (row: SiteRow) => row.opuSiteId ?? '';

export const isSameSite = (a: SiteRow, b: SiteRow) => siteKey(a) === siteKey(b);

export const groupBySite = <T extends SiteRow>(rows: readonly T[]): T[][] => {
  const groups = new Map<string, T[]>();
  rows.forEach((row) => {
    const group = groups.get(siteKey(row));
    if (group) group.push(row);
    else groups.set(siteKey(row), [row]);
  });
  return [...groups.values()];
};

export const numberPerSite = <T extends SiteRow>(rows: readonly T[]) => {
  const counts = new Map<string, number>();
  return rows.map((row) => {
    const donor = (counts.get(siteKey(row)) ?? 0) + 1;
    counts.set(siteKey(row), donor);
    return { ...row, donor, rowNum: donor };
  });
};
